import {
  ByTypeSelectType,
  MentionsSentimentTabType,
} from '@op/pages/Overview/KeyMetrics/constants';
import {
  useDrilldownParams,
  EDrilldownMode,
} from '@op/hooks/useDrilldownParams';
import { echartsContent } from '@op/utils';
import { decimalFormatPercentage } from '@op/utils/value';
import { useRef } from 'react';
import { SentimentMentionsTwoLevelType } from '../../utils';
import { getXAxisAxisLabel } from '@op/utils/chart';
import { DBO_Y } from '@op/utils/format';
import { AnalysisSummaryTypeTab } from '@op/pages/AIAnalysisHub/KeywordAndURLAnalysis/types';
import {
  ChartFootBtnContext,
  useChartFootBtn,
} from '@op/hooks/useChartFootBtn';

/**
 * @deprecated 使用 useChartFootBtn 代替
 * 该 Hook 已迁移至全局公共 Hook @op/hooks/useChartFootBtn
 * 为了向后兼容保留此导出，建议新代码使用 useChartFootBtn
 */
export function useChartClickInfo({
  filter,
  tab,
  by_type,
  hasEventBtn = false,
  eventClick
}: {
  filter: any;
  tab: MentionsSentimentTabType | AnalysisSummaryTypeTab;
  by_type: ByTypeSelectType;
  hasEventBtn?: boolean;
  eventClick?: (params: any) => void;
}) {
  const { goPage } = useDrilldownParams();

  const buttons = [
    {
      key: 'feeds',
      labelId: 'Click to go to [Feeds]',
    },
    {
      key: 'key-opinions',
      labelId: 'Click to go to [Key Opinions]',
    },
  ];
  if (hasEventBtn) {
    buttons.push({
      key: 'key-events',
      labelId: 'Click to view the anomaly analysis',
    });
  }

  return useChartFootBtn({
    filter,
    buttons,
    onClick: (context: ChartFootBtnContext) => {
      const { btnKey, dataAttrs, filter, customData } = context;
      const { tab, by_type } = customData || {};
      const tabMap: {
        [key in MentionsSentimentTabType | AnalysisSummaryTypeTab]?: string;
      } = {
        total: 'socialMedia',
        game_store: 'gameStore',
        news: 'newMedia',
        social_media: 'socialMedia',
      };

      const params = {
        ...filter,
        start_time: dataAttrs.startTime,
        end_time: dataAttrs.endTime,
      };
      if (by_type === 'exclude_low_invalid') {
        params.is_valid = [2, 1];
      }
      if (by_type === 'exclude_neutral') {
        params.sentiment = [5, 1];
      }

      if (btnKey === 'feeds') {
        return goPage({
          path: `/opinion/Feeds/Feeds?tab=${tabMap[tab as MentionsSentimentTabType | AnalysisSummaryTypeTab]}`,
          filter: params,
          // Feeds 为特殊页面，走本地缓存
          mode: EDrilldownMode.Local,
        });
      }
      if (btnKey === 'key-opinions') {
        return goPage({
          path: `/opinion/Overview/KeyOpinions`,
          filter: params,
          // KeyOpinions 为特殊页面，走本地缓存
          mode: EDrilldownMode.Local,
        });
      }
      if (btnKey === 'key-events') {
        eventClick?.(params);
      }
    },
    customData: { tab, by_type },
  });
}

export function useXAxisInfo({
  eventData,
  chartEventSetting,
  data,
  filter,
}: {
  eventData?: { chartRenderEventList: any };
  chartEventSetting?: { showEventAxis: boolean };
  data?: any;
  filter?: any;
}) {
  // 从时间轴中找到对应的key
  function getAxisEventLabel(
    data: { [key: string]: { count: number } },
    key: string,
  ) {
    const info = data[key] || {};
    const value = info.count || 0;

    if (value === 0) return '';
    const flag = value > 99;
    return flag ? '99+' : value;
  }

  function getEventXAxisAxisLabel(
    eventList: {
      [key: string]: { count: number };
    },
    highlightKey?: string,
  ) {
    return {
      margin: -6,
      interval: 0,
      rich: {
        eventShow: {
          fontSize: 10,
          color: 'rgb(131, 134, 151)',
          borderRadius: [8, 8, 8, 8],
          padding: [2, 8, 1, 8],
          backgroundColor: '#fff',
          shadowBlur: 2,
          shadowColor: 'rgba(0, 0, 0, 0.3)',
        },
        eventHighlight: {
          fontSize: 10,
          color: '#FFFFFF',
          borderRadius: [8, 8, 8, 8],
          padding: [2, 8, 1, 8],
          backgroundColor: '#326FF0',
          shadowBlur: 2,
          shadowColor: 'rgba(0, 0, 0, 0.3)',
        },
        eventHide: {},
      },
      splitLine: { show: true },
      formatter: (value: any) => {
        const eventNumber = getAxisEventLabel(eventList, value);
        const eventCss = eventNumber ? 'eventShow' : 'eventHide';
        if (highlightKey === value) {
          return `{eventHighlight|${eventNumber}}`;
        }
        return `{${eventCss}|${eventNumber}}`;
      },
    };
  }

  function getXAxisList() {
    const { showEventAxis } = chartEventSetting || { showEventAxis: false };
    const { chartRenderEventList } = eventData || { chartRenderEventList: {} };
    return [
      {
        show: showEventAxis,
        type: 'category',
        id: 'eventDetails',
        data: data?.xAxisData,
        position: 'bottom',
        triggerEvent: true,
        axisTick: { show: false },
        boundaryGap: true,
        offset: 15,
        axisLabel: getEventXAxisAxisLabel(chartRenderEventList),
        axisLine: { lineStyle: { color: '#D8DAE4' }, onZero: false },
      },
      {
        data: data?.xAxisData,
        position: 'bottom',
        boundaryGap: true,
        // axisTick: { show: false },
        axisLine: { lineStyle: { color: '#D8DAE4' }, onZero: false },
        axisLabel: {
          ...getXAxisAxisLabel(filter),
          margin: showEventAxis ? 26 : 8,
        },
        // 这里高亮坐标
        axisPointer: { label: { show: true, margin: showEventAxis ? 26 : 8 } },
      },
    ];
  }

  return {
    getXAxisList,
    getEventXAxisAxisLabel,
    maxTimeValue: data?.xAxisData?.[data?.xAxisData?.length - 1],
    showEventAxis: chartEventSetting?.showEventAxis || false,
  };
}

export function useYAxisInfo({
  isRatingRatio,
  metrics,
  type,
  showEventBubble,
  data,
}: {
  isRatingRatio?: boolean;
  metrics: SentimentMentionsTwoLevelType | SentimentMentionsTwoLevelType[];
  type: 'bar' | 'line';
  showEventBubble: boolean;
  data: any;
}) {
  const yAxisCustomMap = useRef<{
    [key in SentimentMentionsTwoLevelType]?:
    | { customValues?: Array<number>; min?: number; max?: number }
    | (() => { customValues?: Array<number>; min?: number; max?: number });
  }>({
    mentions_sentiment: () => {
      const sentimentList = data?.yAxisData?.avg_sentiment;

      if (!Array.isArray(sentimentList)) {
        return {
          min: 0,
          max: 5,
          customValues: [0, 1, 2, 3, 4, 5],
        };
      }

      const maxValue = Math.ceil(Math.max(...sentimentList));
      const minValue = Math.floor(Math.min(...sentimentList));
      let customValues = [];
      for (let i = minValue; i <= maxValue; i++) {
        customValues.push(i);
      }

      const len = customValues.length;
      if (len <= 2) {
        customValues = [customValues[0] - 1, ...customValues];
        if (customValues.length < 3)
          customValues.push(customValues[customValues.length - 1] + 1);
      }
      return {
        customValues,
        min: customValues[0],
        max: customValues[customValues.length - 1],
      };
    },
    mobile_selected_avg_score: {
      customValues: [0, 1, 2, 3, 4, 5],
      min: 0,
      max: 5,
    },
    mobile_lifetime_avg_score: {
      customValues: [0, 1, 2, 3, 4, 5],
      min: 0,
      max: 5,
    },
    mentions_brand_health: {
      max: 100,
    },
    negative_mentions_ratio: {
      min: 0,
      max: 1,
    },
  });

  function getYAxisCustomValues(key: SentimentMentionsTwoLevelType) {
    const customValues = yAxisCustomMap.current[key];
    if (customValues) {
      return typeof customValues === 'function' ? customValues() : customValues;
    }
    return {};
  }

  function getYAxisInfo() {
    const metricsList = Array.isArray(metrics) ? metrics : [metrics];
    const yAxisList: Array<any> = [
      {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => {
            if (isRatingRatio) {
              return decimalFormatPercentage(value, '0', 0);
            }

            return echartsContent(value);
          },
          color: '#838697',
        },
        alignTicks: !showEventBubble,
        nameTextStyle: {
          color: '#444444',
          fontWeight: 400,
          fontSize: '12px',
        },
        splitLine: { show: !showEventBubble },
        ...(isRatingRatio ? { max: 1 } : {}),
        splitNumber: 5,
      },
    ];

    if (type === 'bar') {
      const metricsItem = metricsList[0];
      const { customValues, max, min } = getYAxisCustomValues(metricsItem);
      const item: any = {
        type: 'value',
        alignTicks: !showEventBubble,
        splitLine: { show: !showEventBubble },
        axisLabel: {
          formatter: (value: any) => {
            if (metricsList.includes('mentions_sentiment')) {
              return value.toFixed(0);
            }

            if (metricsList.includes('steam_review_positive')) {
              return DBO_Y.percent(value / 100, false);
            }
            if (metricsList.includes('negative_mentions_ratio')) {
              return DBO_Y.percent(value, false);
            }

            return echartsContent(value);
          },
          color: '#838697',
        },
        splitNumber: 5,
      };

      if (customValues) {
        item.axisLabel.customValues = customValues;
      }
      if (min !== undefined) {
        item.min = min;
      }
      if (max) {
        item.max = max;
      }

      yAxisList.push(item);
    }

    return yAxisList;
  }

  return { getYAxisInfo };
}
